import { useTaskTranslation } from '../hooks/useTaskTranslation';
import { FormEvent, useRef, useState } from 'react';
import Modal from '../../../components/UI/Modals/Modal';
import { useAppDispatch } from '../../../store/store';
import {
  createTask,
  CreateTaskParams,
  TASK_PRIORITIES,
  TaskPriority,
} from '../../../store/slices/tasks';
import { toast } from 'react-toastify';
import classes from './CreateTaskModal.module.css';
import { useTaskAssigneeSelection } from '../hooks/useTaskAssigneeSelection';
import { TaskAssigneePicker } from './TaskAssigneePicker';
import { useTaskCustomerSelection } from '../hooks/useTaskCustomerSelection';
import { TaskCustomerPicker } from './TaskCustomerPicker';

export const CreateTaskModal = ({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) => {
  const { translations } = useTaskTranslation();
  const dispatch = useAppDispatch();
  const [pending, setPending] = useState(false);
  const [creationError, setCreationError] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<NonNullable<CreateTaskParams['status']>>('open');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [startedAt, setStartedAt] = useState('');
  const [finishedAt, setFinishedAt] = useState('');
  const customerSelection = useTaskCustomerSelection();
  const assigneeSelection = useTaskAssigneeSelection();
  console.log('assigneeSelection', assigneeSelection);

  const [validationError, setValidationError] = useState('');
  const submitting = useRef(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting.current || pending) return;
    if (!title.trim()) {
      setValidationError(translations('task_board.enter_a_task_title'));
      return;
    }
    const dates = [startedAt, finishedAt];
    if (dates.some((date) => date && Number.isNaN(new Date(date).getTime()))) {
      setValidationError(translations('task_board.enter_valid_task_dates'));
      return;
    }
    setValidationError('');
    setCreationError('');
    submitting.current = true;
    setPending(true);
    try {
      const merchantPersonAssign = assigneeSelection.getAssigneePayload();
      const customer = await customerSelection.getCustomerPayload();
      console.log('beneeee', {
        title: title.trim(),
        status,
        priority,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(startedAt ? { datetimeStart: new Date(startedAt).toISOString() } : {}),
        ...(finishedAt ? { datetimeFinish: new Date(finishedAt).toISOString() } : {}),
        ...(customer ? { customer } : {}),
        merchantPersonAssign,
      });
      await dispatch(
        createTask({
          title: title.trim(),
          status,
          priority,
          ...(description.trim() ? { description: description.trim() } : {}),
          ...(startedAt ? { datetimeStart: new Date(startedAt).toISOString() } : {}),
          ...(finishedAt ? { datetimeFinish: new Date(finishedAt).toISOString() } : {}),
          ...(customer ? { customer } : {}),
          merchantPersonAssign,
        })
      ).unwrap();
      toast.success(translations('task_board.successfully_created_the_task'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      onCreated();
    } catch (error) {
      setCreationError(
        typeof error === 'string'
          ? error
          : error instanceof Error
          ? error.message
          : translations('task_board.unable_to_create_the_task')
      );
    } finally {
      submitting.current = false;
      setPending(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!submitting.current && !pending) onClose();
      }}
      hideCloseButton={pending}
      header={translations('task_board.new_task')}
      divider
      containerPadding="0"
      containerClassName={classes.modalContainer}
      headerStyles={{ padding: '32px 32px 20px', width: '100%' }}
      width="90%"
      maxWidth="640px"
    >
      <form className={classes.form} onSubmit={handleSubmit}>
        <fieldset disabled={pending} className={classes.fields}>
          <label className={classes.field}>
            {translations('task_board.title')}
            <input
              autoFocus
              required
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
          <label className={classes.field}>
            {translations('task_board.description')}
            <textarea
              rows={4}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>
          <label className={classes.field}>
            {translations('task_board.status')}
            <select
              value={status}
              onChange={(event) =>
                setStatus(event.target.value as NonNullable<CreateTaskParams['status']>)
              }
            >
              <option value="open">{translations('task_board.open')}</option>
              <option value="in progress">{translations('task_board.in_progress')}</option>
              <option value="closed">{translations('task_board.closed')}</option>
              <option value="discarded">{translations('task_board.discarded')}</option>
            </select>
          </label>
          <label className={classes.field}>
            {translations('task_board.priority')}
            <select
              value={priority}
              onChange={(event) => setPriority(event.target.value as TaskPriority)}
            >
              {TASK_PRIORITIES.map((value) => (
                <option key={value} value={value}>
                  {translations(`task_board.priority_${value}`)}
                </option>
              ))}
            </select>
          </label>
          <div className={classes.dates}>
            <label className={classes.field}>
              {translations('task_board.start_date_and_time')}
              <input
                type="datetime-local"
                step="any"
                value={startedAt}
                onChange={(event) => setStartedAt(event.target.value)}
              />
            </label>
            <label className={classes.field}>
              {translations('task_board.end_date_and_time')}
              <input
                type="datetime-local"
                step="any"
                value={finishedAt}
                onChange={(event) => setFinishedAt(event.target.value)}
              />
            </label>
          </div>
          <div className={classes.personSection}>
            <TaskCustomerPicker
              selection={customerSelection}
              disabled={pending}
              onSelectionChange={() => {
                setValidationError('');
                setCreationError('');
              }}
            />
          </div>
          <div className={classes.personSection}>
            <TaskAssigneePicker
              selection={assigneeSelection}
              disabled={pending}
              onSelectionChange={() => {
                setValidationError('');
                setCreationError('');
              }}
            />
          </div>
        </fieldset>
        {(validationError || creationError) && (
          <div role="alert" className={classes.error}>
            {validationError || creationError}
          </div>
        )}
        <div className={classes.actions}>
          <button type="button" disabled={pending} onClick={onClose}>
            {translations('task_board.cancel')}
          </button>
          <button
            type="submit"
            disabled={pending || customerSelection.detailsPending}
            className={classes.submit}
          >
            {pending ? translations('task_board.creating') : translations('task_board.create_task')}
          </button>
        </div>
      </form>
    </Modal>
  );
};
